import type {
  ReviewAssignmentStatus,
  ReviewCycleStatus,
  ReviewModel,
  ReviewPotential,
  ReviewQuestion,
  ReviewRelationship,
  ReviewSpaceMode
} from '@/types/performanceReview'
import { addMonths, dayIn, formatDay, isDay, messageOf, statusOf } from '@/components/features/developmentPlans/planUtils'

export { addMonths, dayIn, formatDay, isDay, messageOf, statusOf }

/** The API's limits (`LIMITS` of `performanceReview.service.js`). */
export const REVIEW_LIMITS = {
  title: 120, topic: 80, question: 500, answer: 5000, questions: 40, tag: 80, reminders: 5, reminderMaxDays: 60, label: 40, weightMax: 10, nominationMax: 10,
  scaleMin: 2, scaleMax: 10
} as const

export const RELATIONSHIPS: ReviewRelationship[] = ['SELF', 'MANAGER', 'PEER', 'REPORT']
/** The 9-box potential levels and performance bands, low to high. */
export const POTENTIALS: ReviewPotential[] = ['LOW', 'MEDIUM', 'HIGH']
export const MODELS: ReviewModel[] = ['DEGREES_180', 'DEGREES_90', 'DEGREES_360']

/** The relationships each model brings, as the server resets them on a model change. */
export const MODEL_RELATIONSHIPS: Record<ReviewModel, ReviewRelationship[]> = {
  DEGREES_90: ['MANAGER'],
  DEGREES_180: ['SELF', 'MANAGER'],
  DEGREES_360: ['SELF', 'MANAGER', 'PEER', 'REPORT']
}

/** PEER and REPORT never show their author, and only with this many sent (criteria 14 and 23). */
export const ANONYMOUS_RELATIONSHIPS: ReviewRelationship[] = ['PEER', 'REPORT']

export const CYCLE_STATUS_STYLE: Record<ReviewCycleStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-emerald-50 text-emerald-700',
  CLOSED: 'bg-amber-50 text-amber-700',
  RELEASED: 'bg-blue-50 text-blue-700'
}

export const ASSIGNMENT_STATUS_STYLE: Record<ReviewAssignmentStatus, string> = {
  NOT_STARTED: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-700',
  SUBMITTED: 'bg-emerald-50 text-emerald-700'
}

let questionSeq = 0
/** A local key for a question not saved yet (React list key; the server gives the id). */
export const newQuestionKey = () => `q${Date.now().toString(36)}${(questionSeq += 1)}`

/** `day` plus whole days (UTC arithmetic on a calendar day). */
export const addDays = (day: string, days: number): string => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/** `{ errors: [{ param, message }] }` of a refused call. */
export const apiErrors = (err: unknown): Array<{ param?: string; message?: string }> =>
  (err as { response?: { data?: { errors?: Array<{ param?: string; message?: string }> } } })?.response?.data?.errors ?? []

/** The extra `data` a refusal carries (`withoutManager`, `missingQuestionIds`). */
export const apiData = <T>(err: unknown): T | undefined =>
  (err as { response?: { data?: { data?: T } } })?.response?.data?.data

/** "Um ciclo" opens straight into it; anything else is the list. */
export const reviewSpaceModeOf = (moduleConfig: unknown): ReviewSpaceMode =>
  (moduleConfig && typeof moduleConfig === 'object' && (moduleConfig as { mode?: unknown }).mode === 'single' ? 'single' : 'multi')

/** A period as the reader says it ("1 de jan. de 2026 – 31 de dez. de 2026"). */
export const formatRange = (from: string | null | undefined, to: string | null | undefined, locale: string): string =>
  [formatDay(from, locale), formatDay(to, locale)].filter(Boolean).join(' – ')

/** The basics a new cycle needs: title, the period reviewed and the answering window. */
export interface CycleBasicsDraft {
  title: string
  periodStartsOn: string
  periodEndsOn: string
  opensOn: string
  closesOn: string
}

/** This year as the period, the window from today for two weeks (all editable). */
export const emptyCycleBasics = (timeZone: string | undefined, title: string): CycleBasicsDraft => {
  const today = dayIn(timeZone)
  const year = today.slice(0, 4)
  return { title, periodStartsOn: `${year}-01-01`, periodEndsOn: `${year}-12-31`, opensOn: today, closesOn: addDays(today, 14) }
}

export type CycleBasicsErrors = Partial<Record<keyof CycleBasicsDraft, string>>

/** Checked in the order the fields appear; `null` when valid. Keys are `performanceReview` namespace keys. */
export const validateCycleBasics = (draft: CycleBasicsDraft, t: (key: string) => string): CycleBasicsErrors | null => {
  const errs: CycleBasicsErrors = {}
  if (!draft.title.trim()) errs.title = t('cycleForm.errors.title')
  if (!isDay(draft.periodStartsOn)) errs.periodStartsOn = t('cycleForm.errors.day')
  if (!isDay(draft.periodEndsOn)) errs.periodEndsOn = t('cycleForm.errors.day')
  else if (isDay(draft.periodStartsOn) && draft.periodEndsOn < draft.periodStartsOn) errs.periodEndsOn = t('cycleForm.errors.period')
  if (!isDay(draft.opensOn)) errs.opensOn = t('cycleForm.errors.day')
  if (!isDay(draft.closesOn)) errs.closesOn = t('cycleForm.errors.day')
  else if (isDay(draft.opensOn) && draft.closesOn < draft.opensOn) errs.closesOn = t('cycleForm.errors.window')
  return Object.keys(errs).length ? errs : null
}

/** Field errors out of an API refusal, keyed like the draft. */
export const cycleBasicsApiErrors = (err: unknown): CycleBasicsErrors | null => {
  const out: CycleBasicsErrors = {}
  for (const e of apiErrors(err)) {
    if (e.param && e.message && ['title', 'periodStartsOn', 'periodEndsOn', 'opensOn', 'closesOn'].includes(e.param)) {
      out[e.param as keyof CycleBasicsDraft] = e.message
    }
  }
  return Object.keys(out).length ? out : null
}

/** Whole days from `from` to `to` (`YYYY-MM-DD`), negative when `to` is earlier. */
export const daysBetween = (from: string, to: string): number => {
  const utc = (day: string) => { const [y, m, d] = day.split('-').map(Number); return Date.UTC(y, m - 1, d) }
  return Math.round((utc(to) - utc(from)) / 86_400_000)
}

/** How close a deadline is: red once past, amber on the last three days, plain before. */
export type DeadlineTone = 'default' | 'warning' | 'danger'
export const deadlineTone = (daysLeft: number): DeadlineTone => (daysLeft < 0 ? 'danger' : daysLeft <= 3 ? 'warning' : 'default')
export const DEADLINE_TONE_CLASS: Record<DeadlineTone, string> = {
  default: 'text-gray-700',
  warning: 'text-amber-700',
  danger: 'text-red-600'
}

/** A day without the year when it is this year's ("7 de nov."), so a card's deadline line keeps its countdown. */
export const formatShortDay = (day: string | null | undefined, locale: string, today: string): string => {
  if (!day) return ''
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  const sameYear = today.slice(0, 4) === day.slice(0, 4)
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** A question of the editor, with a stable key for React and the drag. */
export type QuestionDraft = ReviewQuestion & { key: string }
export const topicOf = (q: { topic?: string | null }) => (q.topic ?? '').trim()

/**
 * Questions of the same topic stay together, topics in the order they first
 * appear (stable): the form shows one step per topic, so the editor groups
 * them the same way.
 */
export const groupByTopic = (questions: QuestionDraft[]): QuestionDraft[] => {
  const order: string[] = []
  for (const q of questions) if (!order.includes(topicOf(q))) order.push(topicOf(q))
  return order.flatMap((topic) => questions.filter((q) => topicOf(q) === topic))
}

/** A level of the scale editor (David, 2026-10-29: 2 to 10 typed levels, worst first). */
export interface ScaleLevelDraft { key: string; label: string }
let levelSeq = 0
export const newLevelKey = () => `l${Date.now().toString(36)}${(levelSeq += 1)}`
/** The 5 suggested levels in the reader's language (what an empty `scaleLabels` means on the API). */
export const defaultScaleLabels = (t: (key: string) => string) => [1, 2, 3, 4, 5].map((n) => t(`model.scaleDefault.${n}`))
